import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { startupQuery, type StartupServiceRow } from '../api/devices'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Fila Startup Services - opteemum/devices/details/startupServices.php:
 * cele trei chips, filele System Specific / Startup Services / All Enabled,
 * tabelul cu insigne. Start nu pleaca de aici.
 */

type Pane = 'local' | 'daemons' | 'all'

export function DeviceStartupServices({ id }: ScreenProps) {
  const { data, error } = useQuery(startupQuery(id))
  const [pane, setPane] = useState<Pane>('daemons')

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  const badge = (n: number, tone: string) => (
    <span className={`tab-badge tab-badge-${tone}`}>{n}</span>
  )

  return (
    <>
      <style>{SAS_CSS}</style>
      <div className="sas-section">
        <p className="sas-hint">
          Units configured to start at boot, and whether they are up now. Health only counts long-running
          daemons that are down ({data.stopped} stopped). Oneshots that already ran show as Exited, not
          Stopped.
          {!data.services_live && <> Live running/stopped status fills in after the next agent report.</>}
        </p>
        <div className="sas-chips">
          <div className="sas-chip total">
            <span className="n">{data.enabled}</span>
            <span className="k">Enabled</span>
          </div>
          <div className={`sas-chip ok${data.running === 0 ? ' zero' : ''}`}>
            <span className="n">{data.running}</span>
            <span className="k">Running</span>
          </div>
          <div className={`sas-chip warn${data.stopped === 0 ? ' zero' : ''}`}>
            <span className="n">{data.stopped}</span>
            <span className="k">Stopped</span>
          </div>
        </div>
      </div>

      <div className="sas-section">
        <ul className="nav nav-tabs line-tabs mb-2 sasList" role="tablist">
          <li className={pane === 'local' ? 'active' : undefined}>
            <a
              href="#sas-local"
              data-pane="local"
              onClick={(e) => {
                e.preventDefault()
                setPane('local')
              }}
            >
              <i className="fas fa-server"></i> System Specific{' '}
              {badge(data.counts.local, data.counts.local > 0 ? 'blue' : 'grey')}
            </a>
          </li>
          <li className={pane === 'daemons' ? 'active' : undefined}>
            <a
              href="#sas-daemons"
              data-pane="daemons"
              onClick={(e) => {
                e.preventDefault()
                setPane('daemons')
              }}
            >
              <i className="fas fa-cogs"></i> Startup Services{' '}
              {badge(data.counts.daemons, data.stopped > 0 ? 'amber' : data.counts.daemons > 0 ? 'blue' : 'grey')}
            </a>
          </li>
          <li className={pane === 'all' ? 'active' : undefined}>
            <a
              href="#sas-all"
              data-pane="all"
              onClick={(e) => {
                e.preventDefault()
                setPane('all')
              }}
            >
              <i className="fas fa-list"></i> All Enabled {badge(data.counts.all, data.counts.all > 0 ? 'blue' : 'grey')}
            </a>
          </li>
        </ul>
        <div className="sas-pane">
          <div id="sas-local" className={`sas-panel${pane === 'local' ? ' is-open' : ''}`}>
            <p className="sas-hint">
              Unit files under <code>/etc/systemd/system</code> — added on this box, not shipped by the distro.
            </p>
            <ServiceTable rows={data.groups.local} empty="No local unit files." />
          </div>
          <div id="sas-daemons" className={`sas-panel${pane === 'daemons' ? ' is-open' : ''}`}>
            <p className="sas-hint">
              Long-running automatic services (ssh, cron, nginx, the agent). Stopped here is what turns Health to
              Degraded.
            </p>
            <ServiceTable rows={data.groups.daemons} empty="No automatic services to list yet." />
          </div>
          <div id="sas-all" className={`sas-panel${pane === 'all' ? ' is-open' : ''}`}>
            <p className="sas-hint">
              The same population as <code>systemctl list-unit-files --state=enabled</code>. Oneshots that finished
              correctly are Exited.
            </p>
            <ServiceTable rows={data.groups.all} empty="No automatic services to list yet." />
          </div>
        </div>
      </div>
    </>
  )
}

function ServiceTable({ rows, empty }: { rows: StartupServiceRow[]; empty: string }) {
  if (rows.length === 0) {
    return (
      <div className="alert alert-success" style={{ marginBottom: 0 }}>
        <strong>Great!</strong> {empty}
      </div>
    )
  }

  return (
    <table className="table table-striped table-hover" width="100%">
      <thead>
        <tr>
          <th className="a-left">Service</th>
          <th className="a-left">Status</th>
          <th className="a-left">Type</th>
          <th className="a-left">Start type</th>
          <th className="a-left">Restarts</th>
          <th className="a-left">Action</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((svc) => (
          <tr key={svc.name}>
            <td className="td-small">
              <strong>{svc.display}</strong>
              {svc.display !== svc.name && (
                <div className="text-muted" style={{ fontSize: 11 }}>
                  {svc.name}
                </div>
              )}
              {svc.description !== '' && (
                <div className="text-muted" style={{ fontSize: 11 }}>
                  {svc.description}
                </div>
              )}
              {svc.path !== '' && (
                <div className="text-muted" style={{ fontSize: 11 }}>
                  {svc.path}
                </div>
              )}
            </td>
            <td className="td-small">
              <span className={`label ${svc.badge.cls}`} title={svc.badge.title || undefined}>
                {svc.badge.text}
              </span>
            </td>
            <td className="td-small">{svc.type !== '' ? svc.type : '—'}</td>
            <td className="td-small">{svc.enabled}</td>
            <td className="td-small">{svc.restarts === null ? '—' : svc.restarts}</td>
            <td className="td-small">
              {svc.can_start ? (
                <button className="btn btn-sm btn-success" type="button">
                  <i className="fas fa-undo"></i> Start
                </button>
              ) : svc.restarted ? (
                <span className="metro-btn x-small red">Restarted</span>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export const SAS_CSS = `
    .sas-section { background: #fff; border: 1px solid #e7ecf1; border-radius: 8px; padding: 16px 18px; margin-bottom: 16px; }
    .sas-hint { margin: 0 0 12px; font-size: 12px; color: #7f8c8d; }
    .sas-chips { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 0; }
    .sas-chip { border-radius: 6px; padding: 8px 12px; min-width: 88px; color: #fff; }
    .sas-chip .n { display: block; font-size: 18px; font-weight: 600; line-height: 1.2; }
    .sas-chip .k { display: block; font-size: 11px; letter-spacing: .04em; text-transform: uppercase; opacity: .9; margin-top: 2px; }
    .sas-chip.total { background: #3598dc; }
    .sas-chip.ok { background: #26a65b; }
    .sas-chip.warn { background: #e87e04; }
    .sas-chip.ok.zero { background: #95a5a6; }
    .sas-chip.warn.zero { background: #26a65b; }
    .sas-panel { display: none; }
    .sas-panel.is-open { display: block; }
`
